import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package, Upload, RefreshCw, Loader2, Search, Play, ArrowUpCircle,
  Trash2, ChevronDown, RotateCcw, FileText, CheckCircle, XCircle,
  AlertTriangle, Globe, Server, Filter, History, Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_COLORS = {
  installed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  uninstalled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  to_upgrade: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  to_install: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  to_remove: "bg-red-500/10 text-red-400 border-red-500/20",
};

function ModuleCard({ mod, onInstall, onUpgrade, onUninstall, acting }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10 rounded-lg hover:bg-white/[0.04] transition-all"
      data-testid={`module-card-${mod.name}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${mod.is_app ? "bg-blue-500/10 border border-blue-500/20" : "bg-zinc-800 border border-zinc-700"}`}>
        <Package className={`w-4 h-4 ${mod.is_app ? "text-blue-400" : "text-zinc-500"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white truncate">{mod.display_name || mod.name}</p>
          {mod.is_app && <Badge className="text-[8px] bg-blue-500/10 text-blue-400 border-blue-500/20 px-1">APP</Badge>}
        </div>
        <p className="text-[11px] text-zinc-500 truncate">{mod.name} {mod.installed_version ? `v${mod.installed_version}` : ""}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge className={`text-[10px] ${STATE_COLORS[mod.state] || STATE_COLORS.uninstalled}`}>
          {mod.state}
        </Badge>
        {mod.state === "installed" ? (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              onClick={() => onUpgrade(mod.name)} disabled={acting}
              data-testid={`upgrade-${mod.name}`}>
              <ArrowUpCircle className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={() => onUninstall(mod.name)} disabled={acting}
              data-testid={`uninstall-${mod.name}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : mod.state === "uninstalled" ? (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
            onClick={() => onInstall(mod.name)} disabled={acting}
            data-testid={`install-${mod.name}`}>
            <Play className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function ModuleManager() {
  const { token } = useAuth();
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState("");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchServers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/servers`, { headers });
      setServers(data);
      if (data.length > 0 && !selectedServer) setSelectedServer(data[0].id);
    } catch (err) { toast.error("Failed to load servers"); }
  }, [token]);

  const fetchInstances = useCallback(async () => {
    if (!selectedServer) return;
    try {
      const { data } = await axios.get(`${API}/infrastructure/${selectedServer}`, { headers });
      const insts = data.odoo_instances || [];
      setInstances(insts);
      if (insts.length > 0 && !selectedInstance) setSelectedInstance(insts[0].id);
    } catch (err) { toast.error("Failed to load instances"); }
  }, [selectedServer, token]);

  const fetchModules = useCallback(async () => {
    if (!selectedServer || !selectedInstance) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter !== "all") params.append("state", stateFilter);
      if (search) params.append("search", search);
      const { data } = await axios.get(
        `${API}/modules/${selectedServer}/${selectedInstance}?${params}`,
        { headers }
      );
      setModules(data.modules || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load modules");
    } finally { setLoading(false); }
  }, [selectedServer, selectedInstance, stateFilter, search, token]);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => { fetchInstances(); }, [fetchInstances]);
  useEffect(() => {
    if (selectedServer && selectedInstance) fetchModules();
  }, [selectedServer, selectedInstance, stateFilter, fetchModules]);

  const doModuleAction = async (action, moduleName) => {
    setActing(true);
    try {
      const { data } = await axios.post(
        `${API}/modules/${selectedServer}/${selectedInstance}/${action}`,
        { module_name: moduleName },
        { headers }
      );
      toast.success(data.message);
      setTimeout(fetchModules, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || `${action} failed`);
    } finally { setActing(false); setConfirmAction(null); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/modules/${selectedServer}/${selectedInstance}/upload`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      toast.success(data.message);
      setShowUpload(false);
      setTimeout(fetchModules, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(
        `${API}/modules/${selectedServer}/${selectedInstance}/logs?lines=200`,
        { headers }
      );
      setLogs(data.logs || "No logs available");
      setShowLogs(true);
    } catch (err) { toast.error("Failed to fetch logs"); }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(
        `${API}/modules/${selectedServer}/${selectedInstance}/history`,
        { headers }
      );
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (err) { toast.error("Failed to fetch history"); }
  };

  const handleRestart = async () => {
    setActing(true);
    try {
      const { data } = await axios.post(
        `${API}/modules/${selectedServer}/${selectedInstance}/restart`,
        {},
        { headers }
      );
      toast.success(data.message);
    } catch (err) { toast.error("Restart failed"); }
    finally { setActing(false); }
  };

  const selectedInst = instances.find(i => i.id === selectedInstance);
  const installedCount = modules.filter(m => m.state === "installed").length;
  const appCount = modules.filter(m => m.is_app).length;

  const filteredModules = modules.filter(m => {
    if (search) {
      const q = search.toLowerCase();
      return m.name.toLowerCase().includes(q) || (m.display_name || "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="module-manager-page">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Module Manager</h1>
        <p className="text-sm text-zinc-500 mt-1">Upload, install, upgrade, and manage Odoo modules</p>
      </div>

      {/* Controls Row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedServer} onValueChange={v => { setSelectedServer(v); setSelectedInstance(""); }}>
          <SelectTrigger className="w-60 bg-white/5 border-white/10 text-white" data-testid="server-select">
            <SelectValue placeholder="Select server" />
          </SelectTrigger>
          <SelectContent>
            {servers.map(s => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2"><Server className="w-4 h-4" />{s.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedInstance} onValueChange={setSelectedInstance}>
          <SelectTrigger className="w-60 bg-white/5 border-white/10 text-white" data-testid="instance-select">
            <SelectValue placeholder="Select instance" />
          </SelectTrigger>
          <SelectContent>
            {instances.map(i => (
              <SelectItem key={i.id} value={i.id}>
                <span className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {i.subdomain || i.service_name} ({i.db_name})
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-btn">
          <Upload className="w-4 h-4 mr-2" /> Upload Module
        </Button>
        <Button variant="outline" onClick={handleRestart} disabled={acting} className="border-white/10 text-zinc-400" data-testid="restart-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Restart
        </Button>
        <Button variant="outline" onClick={fetchLogs} className="border-white/10 text-zinc-400" data-testid="logs-btn">
          <FileText className="w-4 h-4 mr-2" /> Logs
        </Button>
        <Button variant="outline" onClick={fetchHistory} className="border-white/10 text-zinc-400" data-testid="history-btn">
          <History className="w-4 h-4 mr-2" /> History
        </Button>
      </div>

      {/* Stats + Search */}
      <div className="flex items-center gap-4 flex-wrap">
        {selectedInst && (
          <div className="flex gap-3">
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">{installedCount} Installed</Badge>
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">{appCount} Apps</Badge>
            <Badge className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">{modules.length} Total</Badge>
          </div>
        )}
        <div className="flex-1" />
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 w-64 bg-white/5 border-white/10 text-white" data-testid="module-search" />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="state-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              <SelectItem value="installed">Installed</SelectItem>
              <SelectItem value="uninstalled">Not Installed</SelectItem>
              <SelectItem value="to_upgrade">To Upgrade</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchModules} className="border-white/10 text-zinc-400" data-testid="refresh-modules-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Module List */}
      <Card className="bg-white/[0.02] border-white/10">
        <CardContent className="p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-zinc-400">Loading modules via XML-RPC...</span>
            </div>
          ) : filteredModules.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">{modules.length === 0 ? "Select an instance to load modules" : "No modules match your filter"}</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
              {filteredModules.map(mod => (
                <ModuleCard key={mod.name} mod={mod}
                  onInstall={name => setConfirmAction({ action: "install", name })}
                  onUpgrade={name => setConfirmAction({ action: "upgrade", name })}
                  onUninstall={name => setConfirmAction({ action: "uninstall", name })}
                  acting={acting} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Upload Odoo Module</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Upload a .zip file containing the module. It will be extracted to the addons path on{" "}
              <span className="text-white font-medium">{selectedInst?.subdomain || selectedInst?.service_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors">
              <Upload className="w-8 h-8 text-zinc-500 mb-2" />
              <span className="text-sm text-zinc-400">{uploading ? "Uploading..." : "Click to select .zip file"}</span>
              <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-input" />
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white">Instance Logs — {selectedInst?.subdomain}</DialogTitle>
          </DialogHeader>
          <pre className="bg-black rounded-lg p-4 text-xs text-green-400 font-mono overflow-auto max-h-[60vh] whitespace-pre-wrap" data-testid="logs-content">
            {logs}
          </pre>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Action History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No actions recorded yet</p>
            ) : history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                {h.status === "success" ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" /> :
                  h.status === "error" ? <XCircle className="w-4 h-4 text-red-400 shrink-0" /> :
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white"><span className="font-medium">{h.action}</span> {h.module_name}</p>
                  <p className="text-[10px] text-zinc-500">{h.performed_by} · {new Date(h.performed_at).toLocaleString()}</p>
                </div>
                <Badge className={`text-[9px] ${h.status === "success" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                  {h.status}
                </Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm {confirmAction?.action}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to <span className="text-white font-medium">{confirmAction?.action}</span> module{" "}
              <span className="text-white font-mono">{confirmAction?.name}</span> on{" "}
              <span className="text-white">{selectedInst?.subdomain || selectedInst?.service_name}</span>?
              {confirmAction?.action === "uninstall" && (
                <span className="block mt-2 text-red-400">This may remove data associated with this module.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="border-white/10 text-zinc-400">Cancel</Button>
            <Button onClick={() => doModuleAction(confirmAction.action, confirmAction.name)}
              disabled={acting}
              className={confirmAction?.action === "uninstall" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
              data-testid="confirm-action-btn">
              {acting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {confirmAction?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
